import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Rows2, Rows3, SearchX } from 'lucide-react';
import { cn } from './Table';
import { SearchField } from './arc/search-field';
import { EmptyState } from './arc/empty-state';
import { Button } from './arc/button';

// DataGrid — tabla del admin con toolbar, orden, densidad, paginado y estados.
//
// Comportamiento: el data-grid de reui (header sticky, columnas ordenables, densidad,
// skeleton sin salto de layout). Estética: la `sortable-data-table` de uiarc.dev
// (header de 44px sin mayúsculas, filas de 52px, bordes sutiles, tinte en la columna
// ordenada). No se usó esa tabla tal cual porque no trae buscador, paginado ni densidad.
// Buscador, empty state y botones SON los de uiarc (src/ui/arc).
//
// El root lleva `tw`: activa los tokens de uiarc y el preflight acotado
// (ver src/ui/arc/foundation.css), así el grid se ve bien en cualquier pantalla.
//
// A propósito NO trae drag&drop de columnas, virtualización ni pinning: sumaría
// @dnd-kit y TanStack a un bundle de CRA para tablas de cientos de filas.

export interface GridCol<T> {
    key: string;
    header: string;
    /** Contenido de la celda. Si falta, se usa `sort` o el campo con el mismo nombre que `key`. */
    render?: (row: T) => React.ReactNode;
    /** Valor para ordenar y buscar. Sin esto la columna no es ordenable. */
    sort?: (row: T) => string | number | null | undefined;
    align?: 'left' | 'right' | 'center';
    /** Clase de ancho (ej 'w-14'); útil para la columna de imagen. */
    width?: string;
    /** No mostrar en pantallas chicas. */
    hideOnMobile?: boolean;
}

// Anchos variados para el skeleton: barras todas iguales se leen como un bloque gris.
const SK_W = ['w-1/2', 'w-3/4', 'w-2/3', 'w-4/5', 'w-3/5'];

const DENSITY = {
    compact: { cell: 'h-10 px-3 py-1', head: 'h-10 px-3' },
    normal: { cell: 'h-[52px] px-4 py-1.5', head: 'h-11 px-4' },
};

export function DataGrid<T extends { id?: string | number }>({
    rows, cols, loading, emptyText = 'Nada por acá todavía', emptyTitle = 'Sin datos', emptyAction,
    searchPlaceholder = 'Buscar…', pageSize = 25, toolbar, onRowClick, rowKey, dense, title, footer,
    renderExpanded, filtersActive, onClearFilters, rowClassName,
}: {
    rows: T[];
    cols: GridCol<T>[];
    loading?: boolean;
    emptyTitle?: string;
    emptyText?: string;
    /** Acción del empty state SIN datos (ej. "Crear el primero"). */
    emptyAction?: React.ReactNode;
    searchPlaceholder?: string;
    pageSize?: number;
    /** Controles extra a la derecha del buscador (botones, filtros). */
    toolbar?: React.ReactNode;
    onRowClick?: (row: T) => void;
    rowKey?: (row: T) => string | number;
    dense?: boolean;
    title?: React.ReactNode;
    footer?: React.ReactNode;
    /** Contenido desplegable de una fila. Si se pasa, cada fila es clickeable para abrir/cerrar.
     *  La tabla de Productos usa este patrón (producto → variantes), así que el grid tiene que
     *  soportarlo para poder migrarla sin perder comportamiento. */
    renderExpanded?: (row: T) => React.ReactNode;
    /** Hay filtros externos activos (los de `toolbar`): el vacío se explica distinto. */
    filtersActive?: boolean;
    onClearFilters?: () => void;
    /** Clase extra por fila (ej. atenuar las que no están en una lista). */
    rowClassName?: (row: T) => string | false | null | undefined;
}) {
    const [openRow, setOpenRow] = useState<string | number | null>(null);
    const [q, setQ] = useState('');
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [dir, setDir] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(0);
    const [density, setDensity] = useState<'compact' | 'normal'>(dense ? 'compact' : 'normal');
    const D = DENSITY[density];

    const searchable = useMemo(() => cols.filter(c => c.sort), [cols]);

    const filtered = useMemo(() => {
        const t = q.trim().toLowerCase();
        if (!t) return rows;
        return rows.filter(r => searchable.some(c => String(c.sort!(r) ?? '').toLowerCase().includes(t)));
    }, [rows, q, searchable]);

    const sorted = useMemo(() => {
        const col = cols.find(c => c.key === sortKey);
        if (!col?.sort) return filtered;
        const mul = dir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const va = col.sort!(a), vb = col.sort!(b);
            // los vacíos siempre al final, no importa la dirección
            if (va == null || va === '') return 1;
            if (vb == null || vb === '') return -1;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
            return String(va).localeCompare(String(vb), 'es', { numeric: true }) * mul;
        });
    }, [filtered, cols, sortKey, dir]);

    const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
    const pageSafe = Math.min(page, pages - 1);
    const slice = sorted.slice(pageSafe * pageSize, pageSafe * pageSize + pageSize);

    const toggleSort = (c: GridCol<T>) => {
        if (!c.sort) return;
        if (sortKey === c.key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortKey(c.key); setDir('asc'); }
        setPage(0);
    };

    const alignCls = (a?: string) => (a === 'right' ? 'text-right tabular-nums' : a === 'center' ? 'text-center' : 'text-left');
    const searching = q.trim() !== '';
    const clearAll = () => { setQ(''); setPage(0); onClearFilters?.(); };

    return (
        <div className="tw min-w-0 overflow-hidden rounded-[var(--radius-surface)] border border-line bg-card shadow-xs">
            {/* Toolbar: buscador flexible + filtros + densidad. Envuelve en mobile sin romper. */}
            <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
                {title && <div className="mr-1 text-[length:var(--text-sm)] font-semibold text-ink">{title}</div>}
                <div className="min-w-[12rem] flex-1">
                    <SearchField label="Buscar" hideLabel value={q} placeholder={searchPlaceholder}
                        onValueChange={v => { setQ(v); setPage(0); }} />
                </div>
                {toolbar}
                <Button variant="ghost" size="sm" type="button" onClick={() => setDensity(d => (d === 'compact' ? 'normal' : 'compact'))}
                    title={density === 'compact' ? 'Filas normales' : 'Filas compactas'} aria-label="Cambiar densidad">
                    {density === 'compact' ? <Rows3 size={16} /> : <Rows2 size={16} />}
                </Button>
            </div>

            {/* overflow-x-auto: en mobile la tabla scrollea sola en vez de romper el layout */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[length:var(--text-sm)]">
                    <thead className="sticky top-0 z-20 bg-card">
                        <tr className="border-b border-line">
                            {cols.map(c => {
                                const sortedHere = sortKey === c.key;
                                return (
                                    <th key={c.key}
                                        onClick={() => toggleSort(c)}
                                        className={cn(
                                            D.head, c.width, alignCls(c.align),
                                            'whitespace-nowrap font-medium text-faint transition-colors',
                                            c.hideOnMobile && 'hidden sm:table-cell',
                                            c.sort && 'cursor-pointer select-none hover:text-ink',
                                            sortedHere && 'bg-muted text-ink',
                                        )}>
                                        <span className={cn('inline-flex items-center gap-1.5', c.align === 'right' && 'flex-row-reverse')}>
                                            {c.header}
                                            {sortedHere && (dir === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Skeleton con la misma altura de fila que los datos: sin salto al cargar. */}
                        {loading && Array.from({ length: 8 }).map((_, i) => (
                            <tr key={`sk-${i}`} className="border-b border-[var(--border-subtle)] last:border-0">
                                {cols.map((c, j) => (
                                    <td key={c.key} className={cn(D.cell, c.hideOnMobile && 'hidden sm:table-cell')}>
                                        <div className={cn('h-3 animate-pulse rounded-full bg-muted', SK_W[(i + j * 2) % SK_W.length])} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {!loading && slice.length === 0 && (
                            <tr><td colSpan={cols.length}>
                                {searching || filtersActive ? (
                                    <EmptyState icon={<SearchX width={22} height={22} strokeWidth={1.5} />}
                                        title="Nada coincide"
                                        description={searching ? `No hay resultados para “${q.trim()}” con los filtros actuales.` : 'Ninguna fila pasa los filtros activos.'}
                                        action={<Button variant="secondary" size="sm" onClick={clearAll}>Limpiar búsqueda y filtros</Button>} />
                                ) : (
                                    <EmptyState title={emptyTitle} description={emptyText} action={emptyAction} />
                                )}
                            </td></tr>
                        )}
                        {!loading && slice.map((r, i) => {
                            const key = rowKey ? rowKey(r) : (r.id ?? i);
                            const isOpen = renderExpanded != null && openRow === key;
                            return (
                                <React.Fragment key={key}>
                                    <tr
                                        onClick={() => {
                                            if (renderExpanded) setOpenRow(isOpen ? null : key);
                                            onRowClick?.(r);
                                        }}
                                        className={cn('group border-b border-[var(--border-subtle)] transition-colors last:border-0 hover:bg-[color-mix(in_oklab,var(--bg-secondary)_55%,transparent)]',
                                            (onRowClick || renderExpanded) && 'cursor-pointer',
                                            isOpen && 'bg-muted', rowClassName?.(r))}>
                                        {cols.map(c => (
                                            <td key={c.key} className={cn(D.cell, alignCls(c.align), 'align-middle text-ink',
                                                c.hideOnMobile && 'hidden sm:table-cell', sortKey === c.key && 'bg-[color-mix(in_oklab,var(--bg-secondary)_45%,transparent)]')}>
                                                {c.render ? c.render(r) : String((c.sort ? c.sort(r) : (r as any)[c.key]) ?? '')}
                                            </td>
                                        ))}
                                    </tr>
                                    {isOpen && (
                                        <tr className="border-b border-line">
                                            <td colSpan={cols.length} className="bg-[color-mix(in_oklab,var(--bg-secondary)_45%,transparent)] p-0">{renderExpanded!(r)}</td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-1.5">
                <div className="text-[length:var(--text-xs)] tabular-nums text-faint">
                    {footer ?? <>{sorted.length} {sorted.length === 1 ? 'fila' : 'filas'}{sorted.length !== rows.length && ` de ${rows.length}`}</>}
                </div>
                {pages > 1 && (
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" disabled={pageSafe === 0} onClick={() => setPage(p => Math.max(0, p - 1))} aria-label="Página anterior"><ChevronLeft size={16} /></Button>
                        <span className="px-1 text-[length:var(--text-xs)] tabular-nums text-soft">{pageSafe + 1} / {pages}</span>
                        <Button variant="ghost" size="sm" disabled={pageSafe >= pages - 1} onClick={() => setPage(p => p + 1)} aria-label="Página siguiente"><ChevronRight size={16} /></Button>
                    </div>
                )}
            </div>
        </div>
    );
}
